import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useStore, type DocEntry } from '../state/store';
import type { AppMode, DocSep } from '../state/sep';
import { sepPreviews, sepPreviewKey, putSepPreview, dropSepFilms } from '../state/bitmaps';
import { engine } from '../worker/client';
import { PREVIEW_MAX, type PlateMeta, type PreviewResult } from '../worker/protocol';
import { filmWindow, filmWindowMoved, type FilmRect } from '../lib/film-view';
import type { Viewport } from '../lib/viewport';

/** How long after the last wheel event the zoom counts as finished. A film crop
 *  costs a full-film rasterisation in the worker, so it is never requested mid-gesture. */
const ZOOM_SETTLE_MS = 150;

/** What the HUD says when the worker could not render the film at all. */
const FILM_ERROR = 'film unavailable';

/**
 * Is a separation actually on the canvas — a plate, the proof or a film —
 * rather than the DTF preview? A build that failed keeps its old plates in the
 * store so the panel can still show them, but the viewer has nothing
 * trustworthy to paint and falls back to the DTF output.
 *
 * Exported so the inspector can gate on exactly the same condition: its check
 * rows toggle overlay masks over the DTF output, which are only paintable while
 * that output is what the canvas is showing.
 */
export function sepViewShown(appMode: AppMode, sep: DocSep | undefined): boolean {
  return appMode === 'sep' && !!sep && !sep.error && sep.plates.length > 0;
}

export interface SepView {
  /** Replace the HUD's Output/Source buttons with the plate bar? Only once a
   *  build has actually produced plates and did not fail. */
  show: boolean;
  plates: PlateMeta[];
  /** The plate actually on screen: `viewer.plate` when that plate still exists
   *  after the last rebuild, else the composite proof. Derived, never written
   *  back to the store. */
  plate: string;
  /** True when the canvas paints a separation view instead of the DTF preview. */
  active: boolean;
  /** True when the canvas paints the 1:1 film instead of a plate preview. */
  filmActive: boolean;
  /** The proof or plate preview, once fetched. */
  preview?: PreviewResult;
  filmW: number | null;
  filmH: number | null;
  /** The crop of the film the cached bitmap covers, in film px. */
  filmRect: FilmRect | null;
  filmBitmap?: ImageBitmap;
  /** The HUD's extra readout: "film 1:1", "building…"/"rebuilding…", "stale". */
  status: string;
  /** Tell the view a pan/zoom/refit has finished, so the film window can move. */
  settle(): void;
  /** Same, debounced: called on every wheel event of a zoom gesture. */
  wheelSettle(): void;
}

/** A film's size and the (doc, plate, build) it belongs to, so a response that
 *  outlived its document, plate or build is never read as the current film. */
interface FilmSize { docId: string; plate: string; epoch: string; width: number; height: number }
interface FilmCropState { docId: string; plate: string; epoch: string; key: string; rect: FilmRect }
/** The crop request currently in flight, if any — the same identity a
 *  `FilmCropState` carries, so both can be compared against a wanted window. */
interface FilmRequest { docId: string; plate: string; epoch: string; rect: FilmRect }

/**
 * Everything the viewer needs in screen-print mode: which plate is on screen,
 * the cached proof/plate/film bitmaps for it, and the film-window bookkeeping
 * that keeps the 1:1 view within one worker crop.
 *
 * It lives beside `Viewer` rather than inside it because the fetches need three
 * effects of their own plus their "latest value" refs, and because the effect
 * ORDER matters: every ref is synced by an effect declared before the fetches
 * that read it, so by the time a request runs on a settle it is reading the
 * viewport that was committed in the same render.
 *
 * Nothing here runs in DTF mode: `show`/`active` are false, every effect
 * returns immediately, and `settle`/`wheelSettle` are no-ops, so the DTF path
 * neither fetches nor re-renders because of this hook.
 */
export function useSepView(doc: DocEntry | undefined, canvasRef: RefObject<HTMLCanvasElement | null>, vp: Viewport | null): SepView {
  const appMode = useStore((s) => s.appMode);
  const platePref = useStore((s) => s.viewer.plate);
  const filmPref = useStore((s) => s.viewer.filmView);

  const inSep = appMode === 'sep';
  const sep = doc?.sep;
  const plates = sep?.plates ?? [];
  const show = sepViewShown(appMode, sep);
  const plate = show && plates.some((p) => p.id === platePref) ? platePref : 'proof';
  const filmActive = show && filmPref && plate !== 'proof';
  const gen = sep?.builtGeneration ?? 0;
  const filmsVersion = sep?.filmsVersion ?? 0;
  const epoch = `${gen}:${filmsVersion}`;
  const kind = plate === 'proof' ? 'proof' : `plate:${plate}`;

  const [previewVersion, setPreviewVersion] = useState(0);
  const [film, setFilm] = useState<FilmSize | null>(null);
  const [filmError, setFilmError] = useState<string | null>(null);
  const [crop, setCrop] = useState<FilmCropState | null>(null);
  const [settleToken, setSettleToken] = useState(0);

  const docId = doc?.id;

  // ---- latest-value refs, declared BEFORE every effect that reads them -------
  const vpRef = useRef<Viewport | null>(null);
  useEffect(() => { vpRef.current = vp; }, [vp]);
  const filmRef = useRef<FilmSize | null>(null);
  useEffect(() => { filmRef.current = film; }, [film]);
  const cropRef = useRef<FilmCropState | null>(null);
  useEffect(() => { cropRef.current = crop; }, [crop]);
  const filmActiveRef = useRef(false);
  useEffect(() => { filmActiveRef.current = filmActive; }, [filmActive]);
  /** The view a response has to still belong to when it lands. A crop request
   *  is NOT tied to the effect run that issued it (see the crop effect): a
   *  settle re-runs that effect several times while one request is in flight,
   *  and a cleanup-scoped `cancelled` flag would throw away a bitmap that is
   *  still exactly the one wanted. */
  const viewRef = useRef({ docId, plate, epoch });
  useEffect(() => { viewRef.current = { docId, plate, epoch }; }, [docId, plate, epoch]);
  /** The crop request awaiting a response. Written directly rather than synced
   *  from state: it has to be visible to the very next settle, which can happen
   *  several renders before the response lands. */
  const requestedRef = useRef<FilmRequest | null>(null);
  /** The view whose film-size probe is awaiting the worker, so the
   *  retry-on-settle below cannot queue a second probe for the SAME view behind
   *  it — while a probe for a different plate or build still goes straight out. */
  const probingRef = useRef<{ docId: string; plate: string; epoch: string } | null>(null);

  const matches = (o: { docId?: string; plate: string; epoch: string } | null) =>
    !!o && o.docId === docId && o.plate === plate && o.epoch === epoch;

  // ---- the proof / plate preview -------------------------------------------
  // Keyed on (doc, kind, builtGeneration, filmsVersion) exactly like the cache,
  // so a cached bitmap is reused and a superseded response is closed rather than
  // cached — the same discipline as the overlay fetch in Viewer.tsx.
  useEffect(() => {
    if (!docId || !show || filmActive) return;
    const key = sepPreviewKey(docId, kind, gen, filmsVersion);
    if (sepPreviews.has(key)) return;
    let cancelled = false;
    void (async () => {
      try {
        const p = plate === 'proof'
          ? await engine().proofPreview(docId, PREVIEW_MAX)
          : await engine().platePreview(docId, plate, PREVIEW_MAX);
        if (!p) return;
        if (cancelled) { p.bitmap.close(); return; }
        putSepPreview(docId, kind, gen, filmsVersion, p);
        setPreviewVersion((v) => v + 1);
      } catch { /* the engine is unavailable or the doc closed: nothing to show */ }
    })();
    return () => { cancelled = true; };
  }, [docId, kind, plate, gen, filmsVersion, show, filmActive]);

  // ---- the film's size ------------------------------------------------------
  // A 1×1 probe: `filmCrop` renders (and caches, worker-side) the whole film and
  // reports its dimensions, which is what the viewport has to be set to before
  // any real window can be chosen.
  //
  // `settleToken` is a dependency purely so a FAILED probe can be retried: a
  // film the worker could not render (an index plate the film size only fits
  // rotated, say) would otherwise leave the view blank for good, with no way
  // back but leaving screen-print mode. With the guard below, a settle costs
  // nothing while the size is already known.
  useEffect(() => {
    if (!docId || !filmActive) {
      setFilm(null); setCrop(null); setFilmError(null);
      requestedRef.current = null; probingRef.current = null;
      // Leaving the film view: the crop is the biggest bitmap the viewer holds
      // and nothing will read it again until the user returns to this plate.
      if (docId) dropSepFilms(docId);
      return;
    }
    if (matches(filmRef.current) || matches(probingRef.current)) return;
    const probing = { docId, plate, epoch };
    probingRef.current = probing;
    setFilm(null); setFilmError(null);
    /** Is this probe still the one the view is waiting for? Scoped to the VIEW
     *  rather than to this effect run, for the same reason the crop request is:
     *  `settleToken` is a dependency (so a failed probe can be retried), so a
     *  click or a scroll during the ~1.7 s a film takes to rasterise re-runs
     *  this effect. A per-run `cancelled` flag would make that click discard
     *  the probe's answer while `probingRef` still suppressed a replacement —
     *  leaving no size, no error and nothing in flight, i.e. a dark canvas
     *  reading "rendering film…" until the user happened to click again. */
    const current = () => probingRef.current === probing && filmActiveRef.current && matches(viewRef.current);
    void (async () => {
      try {
        const probe = await engine().filmCrop(docId, plate, 0, 0, 1, 1);
        probe?.bitmap.close();
        if (!current()) return;
        if (!probe) { setFilmError(FILM_ERROR); return; }
        setFilm({ docId, plate, epoch, width: probe.filmW, height: probe.filmH });
      } catch {
        if (current()) setFilmError(FILM_ERROR);
      } finally {
        if (probingRef.current === probing) probingRef.current = null;
      }
    })();
  }, [docId, plate, epoch, filmActive, settleToken]);

  // ---- the film crop --------------------------------------------------------
  // Deliberately NOT keyed on `vp` (which moves on every frame of a drag) nor on
  // `crop` (which this effect sets): the window is only re-chosen when the view
  // has settled, which `settleToken` marks — a finished pan, a finished zoom, or
  // the refit the viewer does once a new film's size is known.
  //
  // `film` IS a dependency even though the refit that follows it bumps
  // `settleToken` anyway: a rebuild that produces an identically sized film
  // changes no dimension for the viewer to refit to, so without this the new
  // build's window would never be requested and the film would stay blank.
  //
  // `requestedRef` is what keeps that from turning into a request storm. A
  // ResizeObserver delivers a tick per animation frame while a window is being
  // dragged, and each tick refits and settles; without an in-flight record the
  // "has the window moved more than 25 %?" test would keep comparing against
  // the crop already on screen — which the in-flight response has not replaced
  // yet — and re-issue the same window ~60 times a second, each one a 2048²
  // pixel copy plus a `createImageBitmap` in the shared worker.
  //
  // Which is also why a request is scoped to the VIEW (`viewRef`) and not to
  // the effect run that issued it: this effect re-runs on every one of those
  // settles, so a cleanup-scoped `cancelled` flag would discard the very
  // bitmap that is still wanted and leave nothing to show it. A response is
  // dropped only when the doc, the plate or the build has moved under it — and
  // the effect re-run that made it stale has already asked for its replacement.
  useEffect(() => {
    if (!docId || !filmActive) return;
    const size = film, v = vpRef.current, c = canvasRef.current;
    if (!size || !v || !c || !matches(size)) return;
    const want = filmWindow({ x: v.tx, y: v.ty, scale: v.scale, width: c.clientWidth, height: c.clientHeight }, size.width, size.height, PREVIEW_MAX);
    const covered = (o: FilmRequest | FilmCropState | null) => !!o && matches(o) && !filmWindowMoved(o.rect, want);
    if (covered(cropRef.current) || covered(requestedRef.current)) return;
    const cropKind = `film:${plate}:${want.x}:${want.y}:${want.w}:${want.h}`;
    const key = sepPreviewKey(docId, cropKind, gen, filmsVersion);
    const landed = (next: FilmCropState) => {
      // The ref is set alongside the state so the very next settle — which can
      // happen before React has committed `crop` — already sees this window.
      cropRef.current = next;
      setCrop(next);
    };
    if (sepPreviews.has(key)) { landed({ docId, plate, epoch, key, rect: want }); return; }
    const request: FilmRequest = { docId, plate, epoch, rect: want };
    requestedRef.current = request;
    void (async () => {
      try {
        const r = await engine().filmCrop(docId, plate, want.x, want.y, want.w, want.h);
        if (!r) return;
        // Not the newest window any more. `filmCrop` awaits `createImageBitmap`
        // per request, so replies are not guaranteed to come back in the order
        // they were sent; without this an older window's response could land
        // last, evict the newer crop through the per-doc film eviction in
        // `putSepPreview`, and leave the wrong part of the film on screen. It
        // cannot misfire during a tick storm: `covered()` only lets the slot be
        // taken when the window has genuinely moved.
        if (requestedRef.current !== request || !filmActiveRef.current || !matches(viewRef.current)) { r.bitmap.close(); return; }
        // The bitmap is the CROP, not the film: its own size is what the cache
        // entry must record, or a reader would scale it to the whole film.
        putSepPreview(docId, cropKind, gen, filmsVersion, { bitmap: r.bitmap, factor: 1, width: want.w, height: want.h });
        landed({ docId, plate, epoch, key, rect: want });
      } catch { /* unavailable, or the plate went away under us: a settle retries */ }
      finally {
        // Only if it is still ours: a newer request has already taken the slot,
        // and clearing it would let a third one through alongside.
        if (requestedRef.current === request) requestedRef.current = null;
      }
    })();
  }, [docId, plate, gen, filmsVersion, epoch, filmActive, film, settleToken, canvasRef]);

  const settle = useCallback(() => { if (filmActiveRef.current) setSettleToken((t) => t + 1); }, []);
  const wheelTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const wheelSettle = useCallback(() => {
    if (!filmActiveRef.current) return;
    clearTimeout(wheelTimer.current);
    wheelTimer.current = setTimeout(() => setSettleToken((t) => t + 1), ZOOM_SETTLE_MS);
  }, []);
  useEffect(() => () => clearTimeout(wheelTimer.current), []);

  const sizeOk = filmActive && matches(film) ? film : null;
  const cropOk = sizeOk && matches(crop) ? crop : null;
  // The caches are plain Maps, not reactive: `previewVersion` is what makes this
  // lookup run again once a fetch has landed, and `crop` does the same for the film.
  const preview = useMemo(
    () => (show && !filmActive && docId ? sepPreviews.get(sepPreviewKey(docId, kind, gen, filmsVersion)) : undefined),
    [show, filmActive, docId, kind, gen, filmsVersion, previewVersion],
  );
  const filmBitmap = cropOk ? sepPreviews.get(cropOk.key)?.bitmap : undefined;

  // The DTF output moved since these plates were built: they are still the last
  // thing the worker separated, so keep showing them, but say so.
  const stale = !!sep && !!doc?.run && sep.runGeneration !== doc.run.generation;
  const status = [
    filmActive ? 'film 1:1' : '',
    filmActive ? (filmError ?? (filmBitmap ? '' : 'rendering film…')) : '',
    inSep && sep?.building ? (plates.length ? 'rebuilding…' : 'building…') : '',
    show && stale ? 'stale' : '',
  ].filter(Boolean).join(' · ');

  return {
    show, plates, plate, active: show, filmActive,
    preview,
    filmW: sizeOk ? sizeOk.width : null,
    filmH: sizeOk ? sizeOk.height : null,
    filmRect: cropOk ? cropOk.rect : null,
    filmBitmap,
    status, settle, wheelSettle,
  };
}
